import React, { useState, useMemo } from 'react';
import { Landmark, CheckCircle, Info, ShieldAlert } from 'lucide-react';
import { Card } from '../ui';

type EntityType = 'individual' | 'multicandidate_pac' | 'non_multicandidate_pac' | 'state_local_party' | 'national_party';

interface LimitMatrix {
  [donor: string]: {
    [recipient: string]: { limit: string | number; note?: string };
  };
}

// Simplified FEC Limits Matrix (2023-2024 cycle base numbers, rounded/simplified for tool demo purposes)
const LIMITS: LimitMatrix = {
  individual: {
    candidate: { limit: 3300, note: "Per election (Primary and General count separately)" },
    pac: { limit: 5000, note: "Per year" },
    state_party: { limit: 10000, note: "Per year (combined)" },
    national_party: { limit: 41300, note: "Per year" }
  },
  multicandidate_pac: {
    candidate: { limit: 5000, note: "Per election" },
    pac: { limit: 5000, note: "Per year" },
    state_party: { limit: 5000, note: "Per year (combined)" },
    national_party: { limit: 15000, note: "Per year" }
  },
  non_multicandidate_pac: {
    candidate: { limit: 3300, note: "Per election" },
    pac: { limit: 5000, note: "Per year" },
    state_party: { limit: 10000, note: "Per year (combined)" },
    national_party: { limit: 41300, note: "Per year" }
  },
  state_local_party: {
    candidate: { limit: 5000, note: "Per election" },
    pac: { limit: 5000, note: "Per year" },
    state_party: { limit: 'Unlimited', note: "Transfers between affiliated party committees" },
    national_party: { limit: 'Unlimited', note: "Transfers to national party" }
  },
  national_party: {
    candidate: { limit: 5000, note: "Per election" },
    pac: { limit: 5000, note: "Per year" },
    state_party: { limit: 'Unlimited', note: "Transfers between affiliated party committees" },
    national_party: { limit: 'Unlimited', note: "" }
  }
};

const DONOR_OPTIONS = [
  { value: 'individual', label: 'Individual / Individual PAC' },
  { value: 'multicandidate_pac', label: 'Multi-candidate PAC' },
  { value: 'non_multicandidate_pac', label: 'Non-multicandidate PAC' },
  { value: 'state_local_party', label: 'State, District & Local Party' },
  { value: 'national_party', label: 'National Party Committee' }
];

const RECIPIENT_OPTIONS = [
  { value: 'candidate', label: 'Candidate Committee' },
  { value: 'pac', label: 'Another PAC' },
  { value: 'state_party', label: 'State/Local Party Committee' },
  { value: 'national_party', label: 'National Party Committee' }
];

export function PacContributionChecker() {
  const [donor, setDonor] = useState<EntityType>('individual');
  const [recipient, setRecipient] = useState<string>('candidate');

  const model = useMemo(() => {
    const result = LIMITS[donor]?.[recipient];
    if (!result) return { limit: 'Unknown', note: '' };

    const isUnlimited = result.limit === 'Unlimited';
    const formattedLimit = isUnlimited 
      ? 'Unlimited' 
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(result.limit as number);

    return {
      limit: formattedLimit,
      note: result.note,
      isUnlimited
    };
  }, [donor, recipient]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-slate-800 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Landmark className="w-6 h-6 mr-3 text-indigo-300" />
            Federal Contribution Limit Checker
          </h2>
          <p className="text-indigo-100 max-w-xl">
            Quickly check the federal election campaign contribution limits. Select the donor entity type and the recipient to see the legal maximums based on FEC guidelines.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-3">Who is giving the money? (Donor)</label>
            <div className="space-y-2">
              {DONOR_OPTIONS.map(opt => (
                <label 
                  key={opt.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${donor === opt.value ? 'bg-indigo-50 border-indigo-500 dark:bg-indigo-900/20 dark:border-indigo-500' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-100'}`}
                >
                  <input 
                    type="radio" 
                    name="donorType" 
                    value={opt.value} 
                    checked={donor === opt.value}
                    onChange={(e) => setDonor(e.target.value as EntityType)}
                    className="mr-3 accent-indigo-600 w-4 h-4"
                  />
                  <span className="font-semibold text-slate-900 dark:text-white">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-3">Who is receiving the money?</label>
            <div className="space-y-2">
              {RECIPIENT_OPTIONS.map(opt => (
                <label 
                  key={opt.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${recipient === opt.value ? 'bg-emerald-50 border-emerald-500 dark:bg-emerald-900/20 dark:border-emerald-500' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-100'}`}
                >
                  <input 
                    type="radio" 
                    name="recipientType" 
                    value={opt.value} 
                    checked={recipient === opt.value}
                    onChange={(e) => setRecipient(e.target.value)}
                    className="mr-3 accent-emerald-600 w-4 h-4"
                  />
                  <span className="font-semibold text-slate-900 dark:text-white">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white ${model.isUnlimited ? 'bg-emerald-700' : 'bg-slate-900'} min-h-[250px] flex flex-col justify-center`}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Federal Limit</h3>
              <CheckCircle className={`w-8 h-8 ${model.isUnlimited ? 'text-emerald-300' : 'text-indigo-400'}`} />
            </div>
            
            <div className={`text-6xl font-black tracking-tight mb-4 ${model.isUnlimited ? 'text-white' : 'text-indigo-400'}`}>
              {model.limit}
            </div>
            
            {model.note && (
              <div className={`p-3 rounded-lg inline-flex items-center text-sm font-bold ${model.isUnlimited ? 'bg-emerald-800 text-emerald-100' : 'bg-indigo-900/50 text-indigo-200 border border-indigo-500/30'}`}>
                <Info className="w-4 h-4 mr-2 shrink-0" />
                {model.note}
              </div>
            )}
          </Card>

          <Card className="p-4 border-amber-200 dark:border-amber-900 shadow-xl bg-amber-50 dark:bg-amber-900/10 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <p className="font-bold mb-1">Disclaimer</p>
              <p>This tool is for demonstrative modeling purposes only and does not constitute legal advice. FEC limits are subject to change and inflation indexing. Always consult a compliance officer or election attorney before dispersing PAC funds.</p>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
